import React, { useState, useEffect } from 'react';

import ManagerHero from './ManagerHero';
import ManagerValueGauge from './ManagerValueGauge';
import ManagerStreakForm from './ManagerStreakForm';
import ManagerArticles from './ManagerArticles';
import ManagerTacticalConsistency from './ManagerTacticalConsistency';
import ManagerAttackingIntent from './ManagerAttackingIntent';
import ManagerSeasonMomentum from './ManagerSeasonMomentum';
import IdentityGate from '../custom-widgets/IdentityGate';

import sharedStyles from './ManagerShared.module.css';
import styles from './ManagerProfile.module.css';
import { getPlayerAssetUrl,KLAVIYO_PUBLIC_KEY, KLAVIYO_LIST_ID } from '../../../server/services/custom-pages/constants';
import { syncFromKlaviyo, store, subscribeEntityToKlaviyo, getCurrentPublisherName } from '../../../server/services/custom-pages/auth';

export default function ManagerProfilePage({ manager, adId, initialIsMember = false }) {
    console.log("hello",manager);
    const [isMounted, setIsMounted] = useState(false);
    const [isMember, setIsMember] = useState(initialIsMember);
    const [isSyncing, setIsSyncing] = useState(!initialIsMember);
    const [isRevealing, setIsRevealing] = useState(false);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setIsMounted(true);

        syncFromKlaviyo().then(res => {
            if (res.isSignedIn && res.email) {
                setEmail(res.email);
                store.set("fi_was_signed_in", "true");

                // Trust local cookie first to avoid asynchronous Klaviyo indexing lag
                const hasLocalCookie = manager.statscoreId 
                    ? document.cookie.includes(`klaviyo_followed_${manager.statscoreId}=true`)
                    : false;

                const isFollowing = hasLocalCookie || !!(
                    manager.statscoreId &&
                    res.followedEntities &&
                    res.followedEntities.some(e => String(e.id || e) === String(manager.statscoreId))
                );

                if (isFollowing) {
                    setIsMember(true);
                    if (manager.statscoreId) {
                        document.cookie = `klaviyo_followed_${manager.statscoreId}=true; path=/; max-age=${60 * 60 * 24 * 365}`;
                    }
                } else {
                    setIsMember(false);
                    if (manager.statscoreId) {
                        document.cookie = `klaviyo_followed_${manager.statscoreId}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
                    }
                    if (!hasLocalCookie) {
                        document.cookie = "klaviyo_member=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
                    }
                }
            } else {
                // Not signed in to site - rely on cookies
                const isMemberCookie = manager.statscoreId 
                    ? document.cookie.includes(`klaviyo_followed_${manager.statscoreId}=true`)
                    : false;
                if (isMemberCookie || initialIsMember) {
                    setIsMember(true);
                } else {
                    setIsMember(false);
                }
            }
            setIsSyncing(false);
        }).catch(err => {
            console.error('[ManagerProfilePage] sync error:', err);
            setIsSyncing(false);
        });
    }, [initialIsMember, manager.statscoreId]);

    const handleUnlock = async (e) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setEmailError('Please enter a valid email address.');
            return;
        }
        setIsSubmitting(true);
        setEmailError("");
        store.set("user_email", email);

        if (manager.statscoreId) {
            document.cookie = `klaviyo_followed_${manager.statscoreId}=true; path=/; max-age=${60 * 60 * 24 * 365}`;
        }
        document.cookie = 'klaviyo_member=true; path=/; max-age=' + (60 * 60 * 24 * 365);
        setIsRevealing(true);
        setTimeout(() => {
            setIsMember(true);
            setIsRevealing(false);
            setIsSubmitting(false);
        }, 800);

        subscribeEntityToKlaviyo(email, {
            id: manager.statscoreId,
            name: manager.name || manager.display_name || "",
            name_for_digest: manager.display_name || manager.name || "",
            entityType: 'manager',
            currentClubName: manager.currentClubName || null
        }).catch(err => {
            console.warn("[ManagerProfilePage] Klaviyo background subscription:", err);
        });
    };

    return (
        <div className={styles.page} style={{ '--player-frame-url': `url("${getPlayerAssetUrl('frame.jpg')}")` }}>
            <div className={styles.phoneShell} style={{ position: 'relative' }}>
                {/* Hero Section */}
                <ManagerHero manager={manager} />

                {/* STN Video Player */}
                {isMounted && adId !== 'none' && (
                    <section className={styles.managerVideoSection}>
                        <div className={styles.managerVideoPanel}>
                            <div data-stn-player={adId || "j1dzuhuw"} className={styles.managerVideoPlayer} ></div>
                            
                        </div>
                    </section>
                )}

                {/* ── MOBILE LAYOUT ── */}
                <div className={styles.mobileOnly}>
                    <div className={styles.mobileContent}>
                        {/* Identity Gate - Position after video */}
                        <div style={{ position: 'relative' }}>
                            <IdentityGate 
                                isMember={isMember}
                                isRevealing={isRevealing}
                                email={email}
                                setEmail={setEmail}
                                emailError={emailError}
                                setEmailError={setEmailError}
                                isSubmitting={isSubmitting}
                                handleUnlock={handleUnlock}
                                isSyncing={isSyncing}
                            />

                            <div className={!isMember ? styles['blurred-content-locked'] : ''}>
                                {/* Stacked vertical layout as per mobile Figma */}
                                <ManagerValueGauge value={manager.value} />
                                <ManagerTacticalConsistency consistency={manager.tacticalConsistency} />
                                <ManagerSeasonMomentum momentum={manager.momentum} title="Statistics" />
                                <ManagerAttackingIntent intent={manager.attackingIntent} />
                                <ManagerStreakForm streak={manager.streak} />
                            </div>
                            <ManagerArticles articles={manager.articles} />
                        </div>
                    </div>
                </div>

                {/* ── DESKTOP LAYOUT ── */}
                <div className={styles.desktopOnly}>
                    <div className={styles.desktopContent}>
                        {/* Identity Gate - Position after video */}
                        <div style={{ position: 'relative' }}>
                            <IdentityGate 
                                isMember={isMember}
                                isRevealing={isRevealing}
                                email={email}
                                setEmail={setEmail}
                                emailError={emailError}
                                setEmailError={setEmailError}
                                isSubmitting={isSubmitting}
                                handleUnlock={handleUnlock}
                                isSyncing={isSyncing}
                            />

                             <div className={!isMember ? styles['blurred-content-locked'] : ''}>
                                {/* Row 1: Pressure Rating | Attacking Intent | The Streak */}
                                <div className={styles.desktopThreeColGrid}>
                                    <ManagerValueGauge value={manager.value} />
                                    <ManagerAttackingIntent intent={manager.attackingIntent} />
                                    <ManagerStreakForm streak={manager.streak} />
                                </div>

                                {/* Row 2: Tactical Consistency | Statistics */}
                                <div className={styles.desktopThreeColGrid}>
                                    <ManagerTacticalConsistency consistency={manager.tacticalConsistency} />
                                    <ManagerSeasonMomentum momentum={manager.momentum} title="Statistics" />
                                </div>
                            </div>
                            <ManagerArticles articles={manager.articles} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
